// 004 (FR-027, FR-030, FR-031, FR-033, FR-034; BR-1): the delivery address fields under the pin
// picker. Save as Home / School / Other (Other asks for a name). A reverse-geocoded pin fills only
// fields that are empty and that the parent has not typed in (FR-031).
import { useCallback, useEffect, useRef } from "react";
import { StyleSheet, View } from "react-native";

import type { Api } from "@/api/types";
import { PinPicker, type PinValue } from "@/maps/PinPicker";
import type { LatLng, PlaceAddress } from "@/maps/types";
import { makeStyles } from "@/theme/makeStyles";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";
import { TextField } from "@/ui/TextField";

import type { AddressErrors } from "../validation";
import type { AddressFields } from "./addressBody";

const LABELS: { value: Api["AddressLabel"]; text: string }[] = [
  { value: "HOME", text: "Home" },
  { value: "SCHOOL", text: "School" },
  { value: "OTHER", text: "Other" },
];

export const ADDRESS_COPY = {
  flatHouse: "Flat / house no.",
  streetArea: "Street / area",
  landmark: "Landmark (optional)",
  city: "City",
  pincode: "Pincode",
  saveAs: "Save as",
  customLabel: "Name this address",
} as const;

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    root: { gap: t.space.md },
    row: { flexDirection: "row", gap: t.space.sm, flexWrap: "wrap" },
    chip: {
      paddingHorizontal: t.space.md,
      borderRadius: t.radius.pill,
      borderWidth: t.borderWidth.input,
      borderColor: t.color.border,
      backgroundColor: t.color.surface,
    },
    chipOn: {
      borderColor: t.color.primary,
      backgroundColor: t.color.primarySoft,
    },
    half: { flexGrow: 1, flexBasis: 160 },
  }),
);

type FillField = "streetArea" | "city" | "pincode";

export function AddressForm({
  value,
  onChange,
  errors,
  savedPin,
  onPin,
  locate,
}: {
  value: AddressFields;
  onChange: (next: AddressFields) => void;
  errors: AddressErrors;
  savedPin?: LatLng;
  onPin: (pin: PinValue) => void;
  locate?: () => Promise<LatLng | null>;
}) {
  const styles = useStyles();
  // Fields the parent typed in (never overwritten from the pin) and the latest value for the
  // asynchronous reverse-geocode fill. Both are read only in handlers.
  const typed = useRef(new Set<FillField>());
  const latest = useRef(value);
  useEffect(() => {
    latest.current = value;
  });

  function set(k: keyof AddressFields, v: string) {
    if (k === "streetArea" || k === "city" || k === "pincode")
      typed.current.add(k);
    latest.current = { ...value, [k]: v };
    onChange(latest.current);
  }

  const fillFromPin = useCallback(
    (d: PlaceAddress | null) => {
      if (!d) return;
      const next = { ...latest.current };
      const fill = (k: FillField, v: string | null) => {
        if (v && !typed.current.has(k) && next[k].trim() === "") next[k] = v;
      };
      fill("streetArea", d.streetArea ?? d.area);
      fill("city", d.city);
      fill("pincode", d.pincode);
      onChange(next);
    },
    [onChange],
  );

  return (
    <View style={styles.root} testID="address-form">
      <PinPicker
        savedPin={savedPin}
        onChange={onPin}
        onDescribed={fillFromPin}
        error={errors.pin}
        locate={locate}
      />
      <TextField
        label={ADDRESS_COPY.flatHouse}
        value={value.flatHouse}
        onChangeText={(v) => set("flatHouse", v)}
        kind="name"
        error={errors.flatHouse}
      />
      <TextField
        label={ADDRESS_COPY.streetArea}
        value={value.streetArea}
        onChangeText={(v) => set("streetArea", v)}
        kind="name"
        error={errors.streetArea}
      />
      <TextField
        label={ADDRESS_COPY.landmark}
        value={value.landmark}
        onChangeText={(v) => set("landmark", v)}
        kind="name"
        error={errors.landmark}
      />
      <View style={styles.row}>
        <View style={styles.half}>
          <TextField
            label={ADDRESS_COPY.city}
            value={value.city}
            onChangeText={(v) => set("city", v)}
            kind="name"
            error={errors.city}
          />
        </View>
        <View style={styles.half}>
          <TextField
            label={ADDRESS_COPY.pincode}
            value={value.pincode}
            onChangeText={(v) => set("pincode", v)}
            kind="tel"
            error={errors.pincode}
          />
        </View>
      </View>
      <Text weight="semiBold" nativeID="address-label">
        {ADDRESS_COPY.saveAs}
      </Text>
      <View
        style={styles.row}
        role="radiogroup"
        aria-labelledby="address-label"
      >
        {LABELS.map((l) => {
          const on = value.label === l.value;
          return (
            <PressableTarget
              key={l.value}
              accessibilityRole="radio"
              accessibilityLabel={l.text}
              accessibilityState={{ checked: on }}
              aria-checked={on}
              onPress={() => onChange({ ...value, label: l.value })}
              style={on ? [styles.chip, styles.chipOn] : styles.chip}
            >
              <Text weight={on ? "semiBold" : "regular"}>{l.text}</Text>
            </PressableTarget>
          );
        })}
      </View>
      {value.label === "OTHER" ? (
        <TextField
          label={ADDRESS_COPY.customLabel}
          value={value.customLabel}
          onChangeText={(v) => set("customLabel", v)}
          kind="name"
          error={errors.customLabel}
        />
      ) : null}
    </View>
  );
}
